import {
  DOMAIN_TO_REPLACE,
  HALF_DAY_SLOTS_VALUE,
  LOGIN_BUTTON_TEXT,
  LOGIN_TO_CHECK,
  MAP_IMAGE_PREFIX_BY_ENV_TYPE,
  PHONE_NUMBER_MAX_LENGTH,
} from './constants';
import { APPOINTMENT_STATUSES, APPOINTMENT_TYPES, AVAILABLE_SLOTS_TYPES } from './entries';

const REACT_APP_ENV = import.meta.env.VITE_ENV;
const REACT_APP_IMAGE_BASE = import.meta.env.VITE_IMAGE_BASE;
const REACT_APP_MY_ACCOUNT_DOMAIN = import.meta.env.VITE_MY_ACCOUNT_DOMAIN;

export const createAction = (type: string) => (payload?: unknown) => ({ type, payload });

export const capitalizeString = (string: string): string =>
  `${string.charAt(0).toUpperCase()}${string.toLowerCase().slice(1)}`;

export const buildStringFromParts = (parts: string[], delimiter: string): string =>
  parts
    .filter((el) => el)
    .filter((el) => el.replace(/\s/, ''))
    .reduce((result, item, index, arr) => {
      const part = index === arr.length - 1 ? '' : delimiter;
      result += item ? `${item}${part}` : ''; // eslint-disable-line no-param-reassign

      return result;
    }, '');

export const uuidv4 = (): string =>
  (`${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`).replace(/[018]/g, (c) => {
    const charCode = Number(c);
    if (typeof crypto === 'undefined') {
      return (charCode ^ ((window as any).msCrypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (charCode / 4)))).toString(16); // eslint-disable-line
    }
    return (charCode ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (charCode / 4)))).toString(16); // eslint-disable-line
  });

export const trimSpecialCharacters = (data: string): string => data.replace(/^,+|,+$/gm, '');

interface ValidationInput {
  value: string;
  errorMassages: {
    emptyValueMessage: string;
    invalidValueMessage: string;
  };
  validationRegExps: RegExp[];
}

interface ValidationStatus {
  errorMassage: string;
  isValid: boolean;
}

export const getValidationStatus = ({
  value,
  errorMassages: { emptyValueMessage, invalidValueMessage },
  validationRegExps,
}: ValidationInput): ValidationStatus => {
  let validationStatus: ValidationStatus;
  const isValid = validationRegExps.some((regexp) => regexp.test(value));

  if (!value) {
    validationStatus = {
      errorMassage: emptyValueMessage,
      isValid: false,
    };
  } else if (!isValid) {
    validationStatus = {
      errorMassage: invalidValueMessage,
      isValid: false,
    };
  } else {
    validationStatus = {
      errorMassage: '',
      isValid: true,
    };
  }

  return validationStatus;
};

const formatPhoneNumber = (number: string): string => number.replace(/(.{5})(\d{3})/, '$1 $2');
const formatPhoneNumber10Digits = (number: string): string => number.replace(/(.{4})(\d{3})(\d{3})/, '$1 $2 $3');
const formatPhoneNumber11Digits = (number: string): string => number.replace(/(.{5})(\d{3})(\d{3})/, '$1 $2 $3');

export function mapPhoneNumber(number: string): string {
  const valueWithoutSpaces = number.replace(/\s/g, '').slice(0, PHONE_NUMBER_MAX_LENGTH);

  switch (valueWithoutSpaces.length) {
    case 10:
      return formatPhoneNumber10Digits(valueWithoutSpaces);
    case 11:
      return formatPhoneNumber11Digits(valueWithoutSpaces);
    default:
      return formatPhoneNumber(valueWithoutSpaces);
  }
}

export const formatSlot = (slotsType: string, slot: string): string =>
  slotsType === AVAILABLE_SLOTS_TYPES.HalfDay ? HALF_DAY_SLOTS_VALUE[slot as keyof typeof HALF_DAY_SLOTS_VALUE] : slot;

interface AmendAppointmentInfo {
  appointmentInfo: {
    itemId: string;
    orderKey: string;
  };
  contacts: Array<{ contactKey: string; [key: string]: unknown }>;
  selectedContact: string;
  selectedDate: string;
  slotsType: string;
}

interface SlotInfo {
  value: string;
}

interface ProcessedAmendAppointmentData {
  itemId: string;
  postcode: string;
  orderKey: string;
  contact: { contactKey: string; [key: string]: unknown } | undefined;
  date: string;
  slot: string;
}

export const processAmendAppointmentData = (
  amendAppointmentInfo: AmendAppointmentInfo,
  postcode: string,
  slotInfo: SlotInfo | undefined,
): ProcessedAmendAppointmentData => {
  const {
    appointmentInfo: { itemId, orderKey },
    contacts,
    selectedContact,
    selectedDate,
    slotsType,
  } = amendAppointmentInfo;
  const slotValue = slotInfo?.value.split('-')[0];
  const contact = contacts.find(({ contactKey }) => contactKey === selectedContact);

  return {
    itemId,
    postcode,
    orderKey,
    contact,
    date: `${selectedDate}T${slotValue}:00Z`,
    slot: formatSlot(slotsType, slotValue || ''),
  };
};

export const getSafeUrl = (url: string): string => url.replace('http:', 'https:');

export const removeSpacesFromString = (string: string | undefined | null): string | undefined =>
  string?.replace(/\s/g, '');

export const isMaskedInformation = (value: string): boolean => value.includes('*');

export const detectMobile = (): boolean => {
  const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

  return toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem));
};

export const formatAEMImageUri = (uri: string | undefined | null): string => {
  const imageFileName = uri?.slice(uri.lastIndexOf('/')).split('.')[0] || '';

  return `${REACT_APP_IMAGE_BASE}${MAP_IMAGE_PREFIX_BY_ENV_TYPE[REACT_APP_ENV as keyof typeof MAP_IMAGE_PREFIX_BY_ENV_TYPE] || ''}${imageFileName}`;
};

export const formatAEMLinkUrl = (url: string): string =>
  url.replaceAll(DOMAIN_TO_REPLACE, REACT_APP_MY_ACCOUNT_DOMAIN || '');

export const APPOINTMENT_TITLE: Record<string, string> = {
  'Expert set up appointment': 'Expert Setup',
  'Openreach engineer appointment': 'engineer visit',
  'Broadband engineer appointment': 'engineer visit',
};

export const getLoginButtonText = (status: string, type: string): string => {
  if (type === APPOINTMENT_TYPES.expert) {
    return LOGIN_TO_CHECK;
  }
  switch (status) {
    case APPOINTMENT_STATUSES.Booked:
      return LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.Booked];
    case APPOINTMENT_STATUSES.Confirmed:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.Confirmed]} ${APPOINTMENT_TITLE[type]}`;
    case APPOINTMENT_STATUSES.CustomerMissed:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.CustomerMissed]} ${APPOINTMENT_TITLE[type]}`;
    case APPOINTMENT_STATUSES.SupplierMissed:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.SupplierMissed]}`;
    case APPOINTMENT_STATUSES.SupplierChanged:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.SupplierChanged]} ${APPOINTMENT_TITLE[type]}`;
    case APPOINTMENT_STATUSES.AtRisk:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.AtRisk]} ${APPOINTMENT_TITLE[type]}`;
    case APPOINTMENT_STATUSES.SupplierDelayed:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.SupplierDelayed]}`;
    case APPOINTMENT_STATUSES.Rejected:
      return `${LOGIN_BUTTON_TEXT[APPOINTMENT_STATUSES.Rejected]}  ${APPOINTMENT_TITLE[type]}`;
    default:
      return `${APPOINTMENT_TITLE[type] || ''}`;
  }
};
